/**
 * Organization Page
 * Main page for organizing ads into tabs and lists with drag-and-drop
 */

import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { useAllTabs, useCreateTab, useUpdateTab, useDeleteTab, useCreateList, useMoveAd, useReorderAds, organizationKeys } from '../hooks/useOrganization';
import { AdInList, UndoAction } from '../types/organization';
import { SearchBar } from '../components/organization/SearchBar';
import { List } from '../components/organization/List';
import { AdCard } from '../components/organization/AdCard';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import './Organization.css';

export const Organization: React.FC = () => {
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [activeAd, setActiveAd] = useState<AdInList | null>(null);
  const [undoAction, setUndoAction] = useState<UndoAction | null>(null);
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [showYouTubeUploaded, setShowYouTubeUploaded] = useState(true);
  const [showMetaUploaded, setShowMetaUploaded] = useState(true);

  const queryClient = useQueryClient();
  const { data: tabs = [], isLoading } = useAllTabs();
  const createTab = useCreateTab();
  const updateTab = useUpdateTab();
  const deleteTab = useDeleteTab();
  const createList = useCreateList();
  const moveAd = useMoveAd();
  const reorderAds = useReorderAds();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Set active tab on load - restore from localStorage or use first tab
  useEffect(() => {
    if (tabs.length > 0 && !activeTabId) {
      const savedTabId = localStorage.getItem('organization_active_tab');
      // Check if saved tab still exists
      const tabExists = savedTabId && tabs.some(t => t.tab_id === savedTabId);
      setActiveTabId(tabExists ? savedTabId : tabs[0].tab_id);
    }
  }, [tabs, activeTabId]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (activeTabId) {
      localStorage.setItem('organization_active_tab', activeTabId);
    }
  }, [activeTabId]);

  const activeTab = tabs.find((t) => t.tab_id === activeTabId);
  const lists = activeTab?.lists || [];
  const unsortedList = lists.find((l) => l.is_default);

  const handleCreateTab = () => {
    const tabName = prompt('Enter tab name:');
    if (tabName?.trim()) {
      createTab.mutate({ tab_name: tabName.trim() });
    }
  };

  const handleRenameTab = (tabId: string, currentName: string) => {
    const newName = prompt('Enter new tab name:', currentName);
    if (newName?.trim() && newName !== currentName) {
      updateTab.mutate({ tabId, request: { tab_name: newName.trim() } });
    }
  };

  const handleDeleteTab = (tabId: string, tabName: string) => {
    if (window.confirm(`Are you sure you want to delete "${tabName}"? All lists and their contents will be removed from this tab.`)) {
      deleteTab.mutate(tabId, {
        onSuccess: () => {
          if (activeTabId === tabId) {
            setActiveTabId(tabs.find((t) => t.tab_id !== tabId)?.tab_id || null);
          }
        },
      });
    }
  };

  const handleCreateList = () => {
    if (!activeTabId) return;
    
    const listName = prompt('Enter list name:');
    if (listName?.trim()) {
      createList.mutate({ tabId: activeTabId, request: { list_name: listName.trim() } });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const ad = active.data.current?.ad as AdInList;
    if (ad) {
      setActiveAd(ad);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveAd(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!activeData || !overData) return;

    const sourceListId = activeData.listId as string;
    // Extract target list ID - can drop on either a list container or an ad card within a list
    const targetListId = overData.listId as string;

    // Moving between lists - SIMPLE APPROACH
    // Allow dropping on either the list container (type='list') or on an ad card (type='ad')
    if (sourceListId !== targetListId && (overData.type === 'list' || overData.type === 'ad')) {
      const ad = activeData.ad as AdInList;
      
      console.log(`[DRAG] Moving ${ad.ad_id} from ${sourceListId} to ${targetListId}`);
      
      try {
        // Call API and WAIT
        await moveAd.mutateAsync({
          sourceListId,
          adId: ad.ad_id,
          request: { target_list_id: targetListId, position: null },
        });
        
        console.log('[DRAG] Move API success - refetching');
        
        // Refetch affected lists
        await queryClient.refetchQueries({ queryKey: organizationKeys.listAds(sourceListId) });
        await queryClient.refetchQueries({ queryKey: organizationKeys.listAds(targetListId) });
        
        console.log('[DRAG] Refetch complete - setting undo');
        
        // Save undo action
        setUndoAction({
          type: 'move_ad',
          ad_id: ad.ad_id,
          source_list_id: sourceListId,
          target_list_id: targetListId,
          original_position: ad.position,
        });
        
        setShowUndoToast(true);
        setTimeout(() => {
          setShowUndoToast(false);
          setUndoAction(null);
        }, 5000);
        
      } catch (error) {
        console.error('[DRAG] Move failed:', error);
        alert('Failed to move ad. Please try again.');
        queryClient.refetchQueries({ queryKey: organizationKeys.listAds(sourceListId) });
        queryClient.refetchQueries({ queryKey: organizationKeys.listAds(targetListId) });
      }
    }
    // Reordering within same list - keep optimistic (safe, no cross-list issues)
    else if (sourceListId === targetListId && active.id !== over.id) {
      const cachedAds = queryClient.getQueryData<AdInList[]>(organizationKeys.listAds(sourceListId));
      if (!cachedAds) return;

      const activeIndex = cachedAds.findIndex((ad) => ad.ad_id === active.id);
      const overIndex = cachedAds.findIndex((ad) => ad.ad_id === over.id);
      if (activeIndex === -1 || overIndex === -1) return;

      const reorderedAds = arrayMove(cachedAds, activeIndex, overIndex);
      const adPositions = reorderedAds.map((ad, index) => ({
        ad_id: ad.ad_id,
        position: index,
      }));

      queryClient.setQueryData(organizationKeys.listAds(sourceListId), reorderedAds);

      reorderAds.mutate(
        { listId: sourceListId, request: { ad_positions: adPositions } },
        {
          onError: () => {
            queryClient.refetchQueries({ queryKey: organizationKeys.listAds(sourceListId) });
          },
        }
      );
    }
  };

  const handleUndo = async () => {
    if (!undoAction || moveAd.isPending) return;

    if (undoAction.type === 'move_ad') {
      const fromList = undoAction.target_list_id;  // Where it is NOW
      const toList = undoAction.source_list_id;    // Where to RESTORE it
      const adId = undoAction.ad_id;
      
      console.log(`[UNDO] Restoring ${adId} from ${fromList} to ${toList}`);
      
      // Clear undo state
      setUndoAction(null);
      setShowUndoToast(false);

      try {
        // SIMPLE: Call API and WAIT
        await moveAd.mutateAsync({
          sourceListId: fromList,
          adId: adId,
          request: { target_list_id: toList, position: null },
        });
        
        console.log('[UNDO] API success - refetching');
        
        // Refetch ONLY the two affected lists
        await queryClient.refetchQueries({ queryKey: organizationKeys.listAds(fromList) });
        await queryClient.refetchQueries({ queryKey: organizationKeys.listAds(toList) });
        
        console.log('[UNDO] Complete');
        
      } catch (error) {
        console.error('[UNDO] Failed:', error);
        alert('Undo failed. Refreshing lists...');
        await queryClient.refetchQueries({ queryKey: organizationKeys.listAds(fromList) });
        await queryClient.refetchQueries({ queryKey: organizationKeys.listAds(toList) });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="organization-page">
        <div className="loading-state">Loading organization...</div>
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="organization-page">
        <div className="empty-state">
          <h2>Nothing here yet</h2>
          <p>Create your first tab to start organizing ads</p>
          <button onClick={handleCreateTab} className="btn-primary">
            Create Tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="organization-page">
        {/* Tab Bar */}
        <div className="tab-bar">
          {tabs.map((tab) => (
            <div
              key={tab.tab_id}
              className={`tab-item ${activeTabId === tab.tab_id ? 'active' : ''}`}
              onClick={() => setActiveTabId(tab.tab_id)}
            >
              <span className="tab-name" onDoubleClick={() => handleRenameTab(tab.tab_id, tab.tab_name)}>
                {tab.tab_name}
              </span>
              <button
                className="tab-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTab(tab.tab_id, tab.tab_name);
                }}
                title="Delete tab"
              >
                ✕
              </button>
            </div>
          ))}
          <button onClick={handleCreateTab} className="new-tab-btn" title="Create new tab">
            + New Tab
          </button>
        </div>

        {/* Search Bar and New List Button */}
        <div className="toolbar">
          <SearchBar tabId={activeTabId || ''} unsortedListId={unsortedList?.list_id || null} />
          <div className="toolbar-actions">
            <div className="hide-uploaded-controls">
              <span className="hide-uploaded-label">Hide uploaded ads:</span>
              <button
                className={`platform-toggle ${showYouTubeUploaded ? 'active' : 'inactive'}`}
                onClick={() => setShowYouTubeUploaded(!showYouTubeUploaded)}
                title={showYouTubeUploaded ? 'YouTube ads visible - click to hide' : 'YouTube ads hidden - click to show'}
              >
                <YouTubeIcon sx={{ fontSize: 20, color: showYouTubeUploaded ? '#FF0000' : '#666' }} />
              </button>
              <button
                className={`platform-toggle ${showMetaUploaded ? 'active' : 'inactive'}`}
                onClick={() => setShowMetaUploaded(!showMetaUploaded)}
                title={showMetaUploaded ? 'Meta ads visible - click to hide' : 'Meta ads hidden - click to show'}
              >
                <FacebookIcon sx={{ fontSize: 20, color: showMetaUploaded ? '#1877F2' : '#666' }} />
              </button>
            </div>
            <button onClick={handleCreateList} className="btn-secondary" disabled={!activeTabId}>
              + New List
            </button>
          </div>
        </div>

        {/* Lists Container */}
        <div className="lists-container">
          {lists.length === 0 ? (
            <div className="lists-empty">
              <p>No lists in this tab yet</p>
            </div>
          ) : (
            lists.map((list) => (
              <List 
                key={list.list_id} 
                list={list} 
                tabId={activeTabId || ''} 
                showYouTubeUploaded={showYouTubeUploaded}
                showMetaUploaded={showMetaUploaded}
              />
            ))
          )}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeAd ? <AdCard ad={activeAd} listId="" /> : null}
        </DragOverlay>

        {/* Loading Overlay */}
        {moveAd.isPending && (
          <div className="loading-overlay">
            <div className="loading-spinner">Moving ad...</div>
          </div>
        )}

        {/* Undo Toast */}
        {showUndoToast && undoAction && (
          <div className="undo-toast">
            <span>Ad moved</span>
            <button 
              onClick={handleUndo} 
              className="undo-btn"
              disabled={moveAd.isPending}
            >
              Undo
            </button>
            <button onClick={() => {
              setShowUndoToast(false);
              setUndoAction(null);
            }} className="toast-close-btn">
              ✕
            </button>
          </div>
        )}
      </div>
    </DndContext>
  );
};

